import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
    Alert,
    Box,
    FormControlLabel,
    Switch,
    Typography,
} from '@mui/material';
import { CTableWrapper, DateRange, DDateRangePicker } from '../../../../dum';
import { formatDate, formatFloat } from '../../../../../helpers';
import { useProductInventoryMovementsQuery } from '../../../../../services';

// Default drill-down window. A product ledger never pulls all history on open;
// the user widens the range from the picker when they need more.
const DEFAULT_WINDOW_DAYS = 21;

const MOVEMENT_LABEL: Record<string, string> = {
    production: 'Producción',
    sale: 'Venta',
    adjustment: 'Ajuste',
    consumption: 'Consumo',
};

interface IProductInventoryMovementsPanel {
    productId: number;
}

// The per-product ledger behind an inventory balance. Rendered only when its
// collapsible row is open (CTableWrapper mounts the collapse content lazily), so
// the query fires exactly once per open — not for every product on the page.
export function ProductInventoryMovementsPanel({
    productId,
}: IProductInventoryMovementsPanel) {
    const [range, setRange] = useState<DateRange>(() => [
        dayjs().subtract(DEFAULT_WINDOW_DAYS, 'day'),
        dayjs(),
    ]);
    const [includeAllSaleStatuses, setIncludeAllSaleStatuses] = useState(false);

    const { data, loading, error } = useProductInventoryMovementsQuery({
        variables: {
            product_id: productId,
            startDate: range[0].startOf('day').toISOString(),
            endDate: range[1].endOf('day').toISOString(),
            includeAllSaleStatuses,
        },
        // Show cached rows immediately while a background refetch runs when the
        // range or toggle changes.
        fetchPolicy: 'cache-and-network',
    });

    const movements = data?.getProductInventoryMovements ?? [];
    const hasCommitted = movements.some((m) => !m.affects_inventory);

    return (
        <Box>
            {/* Controls pinned at the top of the ledger: the historic date
                range next to the include-all-sales switch. */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    mt: 2,
                    mb: 2,
                }}
            >
                <Box sx={{ minWidth: 240 }}>
                    <DDateRangePicker
                        label={'Rango (última actualización)'}
                        value={range}
                        onChange={setRange}
                        disableGutters
                    />
                </Box>
                <FormControlLabel
                    control={
                        <Switch
                            checked={includeAllSaleStatuses}
                            onChange={(_, checked) =>
                                setIncludeAllSaleStatuses(checked)
                            }
                        />
                    }
                    label={'Incluir todas las ventas (no solo entregadas)'}
                />
            </Box>

            {includeAllSaleStatuses && hasCommitted ? (
                <Alert severity={'info'} sx={{ mb: 2 }}>
                    Las ventas no entregadas se muestran solo como referencia: no
                    afectan el inventario hasta que se marcan como Entregado, por
                    eso no modifican el saldo reconstruido.
                </Alert>
            ) : null}

            {error ? (
                <Alert severity={'error'}>
                    No se pudieron cargar los movimientos.
                </Alert>
            ) : (
                <CTableWrapper
                    stickyHeader={false}
                    // Skeleton (5 rows) on first load so the panel reserves its
                    // height instead of jumping when the rows arrive; on a
                    // refetch the prior rows stay put (data is non-empty).
                    loading={loading}
                    items={[
                        {
                            title: 'Tipo',
                            textAlign: 'left',
                            cellGrow: 3,
                            content: (m) =>
                                MOVEMENT_LABEL[m.movement_type] ??
                                m.movement_type,
                        },
                        {
                            title: 'Última actualización',
                            textAlign: 'left',
                            cellGrow: 3,
                            content: (m) =>
                                m.updated_at ? formatDate(m.updated_at) : '-',
                        },
                        {
                            title: 'Documento',
                            textAlign: 'left',
                            cellGrow: 3,
                            // Only sales carry a document folio (reusing
                            // OrderSale's compound_order_code). Production and
                            // adjustment movements have none — show a dash.
                            content: (m) => m.compound_order_code || '-',
                        },
                        {
                            title: 'Kilos',
                            textAlign: 'right',
                            content: (m) => (
                                <MovementValue
                                    value={m.kilos}
                                    committed={!m.affects_inventory}
                                />
                            ),
                        },
                        {
                            title: 'Grupos',
                            textAlign: 'right',
                            content: (m) => (
                                <MovementValue
                                    value={m.groups}
                                    committed={!m.affects_inventory}
                                />
                            ),
                        },
                        {
                            title: 'Inv. kilos',
                            textAlign: 'right',
                            content: (m) =>
                                m.affects_inventory && m.balance_kilos != null
                                    ? formatFloat(m.balance_kilos)
                                    : '—',
                        },
                        {
                            title: 'Inv. grupos',
                            textAlign: 'right',
                            content: (m) =>
                                m.affects_inventory && m.balance_groups != null
                                    ? formatFloat(m.balance_groups)
                                    : '—',
                        },
                    ]}
                    data={movements}
                />
            )}

            {/* The reconstructed level is the stock immediately BEFORE each
                movement, walked backward from the product's current balance —
                so the newest row shows what the stock was before that change. */}
            <Typography
                variant={'caption'}
                color={'text.secondary'}
                sx={{ display: 'block', mt: 1 }}
            >
                Ordenado por última actualización (más reciente primero). «Inv.
                kilos/grupos» es el inventario reconstruido justo antes de cada
                movimiento, partiendo del saldo actual y deshaciendo cada cambio
                en ese mismo orden.
            </Typography>
        </Box>
    );
}

function MovementValue({
    value,
    committed,
}: {
    value: number;
    committed?: boolean;
}) {
    // Committed (not-yet-delivered) sales are shown in blue with no sign: they
    // are informational and do not move inventory, so a +/− would wrongly imply
    // an effect on the balance.
    if (committed) {
        return (
            <Typography component={'span'} variant={'body2'} color={'info.main'}>
                {formatFloat(Math.abs(value))}
            </Typography>
        );
    }
    const color =
        value < 0 ? 'error.main' : value > 0 ? 'success.main' : 'text.primary';
    return (
        <Typography component={'span'} variant={'body2'} color={color}>
            {value > 0 ? '+' : ''}
            {formatFloat(value)}
        </Typography>
    );
}
